import { useBetterFeatureClient } from "@di-devs/utils-fe";
import type { ReactNode } from "react";
import { createContext, useContext, useMemo, useEffect } from "react";
import type { FeatureClient } from "@/config/client";
import {
	useFaviconQuery,
	useLogoQuery,
	usePublicSettingsQuery,
	useStorePicQuery,
	useSystemSettingsQuery,
} from "@/queries";
import {
	deepCopy,
	initialPublicSettings,
	transformPublicSettings,
	GET_LOCAL_STORAGE_OR_DEFAULT,
	LOCAL_STORAGE_PUBLIC_SETTINGS_KEY,
} from "@/utils/common";

const LayoutConfigContext = createContext<LayoutConfigCtx>({
	logo: undefined,
	favicon: undefined,
	storePic: undefined,
	publicSettings: undefined,
});

export const useLayoutConfigContext = () => {
	const layoutConfig = useContext(LayoutConfigContext);
	if (!layoutConfig) {
		throw new Error("LayoutConfigContext: No value provided");
	}

	return layoutConfig;
};

export const LayoutConfigProvider = ({ children }: { children: ReactNode }) => {
	const reactFeatureClient = useBetterFeatureClient<FeatureClient>();
	const logoQuery = useLogoQuery(reactFeatureClient);
	const faviconQuery = useFaviconQuery(reactFeatureClient);
	const storePicQuery = useStorePicQuery(reactFeatureClient);
	const publicSettingsQuery = usePublicSettingsQuery(reactFeatureClient);
	// TODO: We need to get system settings for client
	const systemSettingsQuery = useSystemSettingsQuery(reactFeatureClient);

	const cachedPublicSettings = useMemo(
		() =>
			GET_LOCAL_STORAGE_OR_DEFAULT(LOCAL_STORAGE_PUBLIC_SETTINGS_KEY, {}),
		[],
	);

	const layoutConfig = useMemo(() => {
		// Note invalidating publicSettings will still be overwritten with cached system settings
		const latestPublicSettings = {
			...deepCopy(initialPublicSettings),
			...cachedPublicSettings,
			...transformPublicSettings(publicSettingsQuery?.data?.data), // Used for client
			...transformPublicSettings(systemSettingsQuery?.data?.data?.data), // Used for admin
		};
		return {
			logo: logoQuery?.data?.data?.data,
			favicon: faviconQuery?.data?.data?.data,
			storePic: storePicQuery?.data?.data?.data,
			publicSettings: latestPublicSettings,
		};
	}, [
		logoQuery?.data?.data,
		faviconQuery?.data?.data,
		storePicQuery?.data?.data,
		publicSettingsQuery?.data?.data,
		systemSettingsQuery?.data?.data,
		cachedPublicSettings,
	]);

	useEffect(() => {
		if (layoutConfig?.publicSettings) {
			localStorage.setItem(
				LOCAL_STORAGE_PUBLIC_SETTINGS_KEY,
				JSON.stringify(layoutConfig.publicSettings),
			);
		}
	}, [layoutConfig?.publicSettings]);

	return (
		<LayoutConfigContext.Provider value={layoutConfig}>
			{children}
		</LayoutConfigContext.Provider>
	);
};
